/**
 * Roter Starburst-Zackenkreis mit weißem Text.
 * Wird im Hero rechts und in der Urgency-Sektion wiederverwendet.
 *
 * Umsetzung: Vollständig inline-SVG mit trigonometrisch berechnetem
 * Zacken-Path. Keine externen Assets, keine PNGs.
 */

type Props = {
    text: string;
    /** Durchmesser in px (default 260). Wird responsiv skaliert. */
    size?: number;
    /** Rotationsoffset in Grad (default -6°). */
    tilt?: number;
    /** Anzahl der Zacken (default 16). */
    spikes?: number;
    /** Farbe des Siegels (default rot). */
    color?: string;
    testId?: string;
};

function starburstPath(cx: number, cy: number, spikes: number, rOuter: number, rInner: number): string {
    const step = Math.PI / spikes;
    let d = "";
    for (let i = 0; i < spikes * 2; i++) {
        const r = i % 2 === 0 ? rOuter : rInner;
        const a = -Math.PI / 2 + step * i;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        d += (i === 0 ? "M" : "L") + x.toFixed(2) + " " + y.toFixed(2) + " ";
    }
    return d + "Z";
}

export default function AktionSeal({
    text,
    size = 260,
    tilt = -6,
    spikes = 16,
    color = "#DC2626",
    testId = "aktion-seal",
}: Props) {
    const cx = 130, cy = 130;
    const outer = 128;
    const inner = 108;
    const outerPath = starburstPath(cx, cy, spikes, outer, inner);
    const innerPath = starburstPath(cx, cy, spikes, outer - 8, inner - 8);
    const lines = text.split(/[·|]/).map((w) => w.trim()).filter(Boolean);
    return (
        <div
            className="hf-aktion-seal relative inline-block"
            style={{ width: size, height: size, transform: `rotate(${tilt}deg)` }}
            data-testid={testId}
        >
            <svg viewBox="0 0 260 260" width="100%" height="100%" role="img" aria-label={text}>
                <path d={outerPath} fill={color} />
                <path d={innerPath} fill="none" stroke="#ffffff" strokeWidth="3" />
            </svg>
            {/* Text als HTML-Overlay — nutzt Flexbox-Layout und wrap-freundliches
             *  Line-Height, damit auch 2-3 Zeilen sauber innerhalb der Zacken sitzen. */}
            <div
                aria-hidden
                className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
            >
                {lines.length === 0 ? null : lines.length === 1 ? (
                    <span
                        className="font-anton text-white uppercase leading-tight"
                        style={{ fontSize: size * 0.11 }}
                    >
                        {lines[0]}
                    </span>
                ) : (
                    lines.map((ln, i) => (
                        <span
                            key={i}
                            className="font-anton text-white uppercase leading-tight"
                            style={{
                                fontSize: size * (i === 0 ? 0.085 : 0.075),
                                letterSpacing: "0.03em",
                                marginTop: i === 0 ? 0 : 2,
                                padding: "0 4%",
                            }}
                        >
                            {ln}
                        </span>
                    ))
                )}
            </div>
        </div>
    );
}
